import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Check, Loader2, XCircle, ChevronLeft, ChevronRight, ChevronDown, Lock, X, CreditCard } from 'lucide-react';
import SEO from '../components/SEO';
import { validatePhone, formatPhoneInput } from '../utils/phoneValidation';
import FieldError from '../components/FieldError';
import PhoneValid from '../components/PhoneValid';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConsentCheckbox from '../components/ConsentCheckbox';
import TrialSignupModal from '../components/TrialSignupModal';
import OffersMarketedSection from '../components/join-club/OffersMarketedSection';
import { trackFBEvent } from '../components/CookieConsent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQ_DATA = [
  { q: 'אני צריך להעלות סרטונים, להיחשף או לדבר למצלמה?', a: 'ממש לא. המודל מיועד לאנשים שאוהבים לעבוד מאחורי הקלעים. אתה מתמקד בניהול קמפיינים ממומנים (PPC), קניית טראפיק ובניית אוטומציות.' },
  { q: 'מה לגבי מכירות ושירות לקוחות?', a: 'אפס חיכוך. אתה לא מתקשר ללידים ולא מעניק שירות. צוותי המכירות והשירות של בעלי ההצעות מטפלים בכל ליד שנכנס. ברגע שנסגרת עסקה, העמלה מופיעה אצלך בדשבורד באופן אוטומטי.' },
  { q: 'כמה תקציב מדיה מומלץ להביא להתחלה?', a: 'מומלץ להגיע עם תקציב של סביב 12,000 ש"ח לטובת טסטים ואופטימיזציה ראשונית, עד הגעה לאיזון (Break-Even) ומשם לסקייל.' },
  { q: 'למה להצטרף למועדון ולא לעבוד לבד?', a: 'כשאתה עובד לבד מול רשתות, מציעים לך מוצרים שאין לך מושג מה ההיתכנות שלהם וללא ליווי. במועדון אתה מקבל הצעות שמותאמות למשפך שלך, ליווי ארכיטקט צמוד וקהילת עמיתים שדוחפת אותך לרווחיות.' },
];

/* ── Reveal: fade-in + slide-up on scroll (IntersectionObserver) ── */
const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
};

/* ── Hero image carousel with blur+fade crossfade transitions ── */
const HERO_IMAGES = [
  { src: 'https://customer-assets.emergentagent.com/job_cc2bfc43-f614-47fc-95b6-bd08d67526e8/artifacts/ht9msp0c_%D7%AA%D7%9E%D7%95%D7%A0%D7%AA%20%D7%A7%D7%94%D7%9C%20%D7%90%D7%99%D7%9B%D7%95%D7%AA%20%D7%A0%D7%9E%D7%95%D7%9B%D7%94.jpg', alt: 'כנס משווקים' },
  { src: 'https://customer-assets.emergentagent.com/job_cc2bfc43-f614-47fc-95b6-bd08d67526e8/artifacts/rqt1vg42_image.png', alt: 'מפגש זום של המועדון' },
  { src: 'https://customer-assets.emergentagent.com/job_cc2bfc43-f614-47fc-95b6-bd08d67526e8/artifacts/buv5grmv_%D7%94%D7%A2%D7%9E%D7%9C%D7%94%20%D7%94%D7%A8%D7%90%D7%A9%D7%95%D7%A0%D7%94%20%D7%A9%D7%9C%20%D7%9E%D7%A9%D7%95%D7%95%D7%A7%20%D7%91%D7%9E%D7%95%D7%A2%D7%93%D7%95%D7%9F.jpeg', alt: 'העמלה הראשונה של משווק במועדון' },
  { src: 'https://customer-assets.emergentagent.com/job_cc2bfc43-f614-47fc-95b6-bd08d67526e8/artifacts/3umhl85z_%D7%9C%D7%99%D7%93%D7%99%D7%9D%20%D7%A8%D7%90%D7%A9%D7%95%D7%A0%D7%99%D7%9D%20%D7%9C%D7%A0%D7%9B%D7%A1%20%D7%A9%D7%9C%20%D7%9E%D7%A9%D7%95%D7%95%D7%A7%20%D7%91%D7%9E%D7%95%D7%A2%D7%93%D7%95%D7%9F.jpeg', alt: 'לידים ראשונים לנכס של משווק במועדון' },
  { src: 'https://customer-assets.emergentagent.com/job_cc2bfc43-f614-47fc-95b6-bd08d67526e8/artifacts/33gfl0dr_%D7%A9%D7%99%D7%97%20%D7%91%D7%99%D7%9F%20%D7%9E%D7%A9%D7%95%D7%95%D7%A7%D7%99%D7%9D%20%D7%91%D7%9E%D7%95%D7%A2%D7%93%D7%95%D7%9F.jpeg', alt: 'שיח בין משווקים במועדון' },
];

const HeroCarousel = ({ compact = false }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % HERO_IMAGES.length), 4000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div className={compact ? 'w-full' : 'max-w-2xl mx-auto mb-10'} data-testid="hero-carousel">
      <div
        className={`relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-[#1a1a2e] ${compact ? 'aspect-[4/5] md:aspect-square' : 'aspect-[16/10] md:aspect-[16/9]'}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {HERO_IMAGES.map((img, i) => (
          <img
            key={i}
            src={img.src}
            alt={img.alt}
            loading={i === 0 ? 'eager' : 'lazy'}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-1200 ease-out ${
              i === active ? 'opacity-100 blur-0 scale-100' : 'opacity-0 blur-md scale-105 pointer-events-none'
            }`}
            data-testid={`hero-slide-${i}`}
          />
        ))}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5" role="tablist" aria-label="גלריית תמונות">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`עבור לתמונה ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-7 bg-[#C5366A]' : 'w-1.5 bg-white/50 hover:bg-white/80'}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Floating "X. Y. הצטרף 🔥" social-proof bubble (cycles every 5s) ── */
const JoinedBubble = () => {
  const [joiners, setJoiners] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/payment/recent-club-joiners?limit=20`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setJoiners(d.joiners || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (dismissed || joiners.length === 0) return undefined;
    // initial show: short delay so it doesn't pop the moment the page loads
    const showTimer = setTimeout(() => setVisible(true), 1800);
    return () => clearTimeout(showTimer);
  }, [joiners, dismissed]);

  useEffect(() => {
    if (dismissed || joiners.length === 0 || !visible) return undefined;
    // Every 5s: hide, advance, then show next
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % joiners.length);
        setVisible(true);
      }, 350);
    }, 5000);
    return () => clearInterval(interval);
  }, [joiners, visible, dismissed]);

  if (dismissed || joiners.length === 0) return null;
  const current = joiners[idx];

  return (
    <div
      className={`fixed bottom-5 right-4 md:right-6 z-40 transition-all duration-400 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'}`}
      data-testid="joined-bubble"
      dir="rtl"
    >
      <div className="flex items-center gap-2 bg-[#0f0f13]/95 backdrop-blur-md border border-[#C5366A]/40 text-white px-3 py-2 md:px-4 md:py-2.5 rounded-full shadow-xl shadow-[#C5366A]/20 max-w-[280px]">
        <div className="flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full bg-gradient-to-br from-[#C5366A] to-[#ff6b9d] text-white text-xs md:text-sm font-black flex items-center justify-center">
          {current.initials.replace(/\./g, '').replace(/\s+/g, '')}
        </div>
        <div className="flex items-center gap-1.5 text-xs md:text-sm">
          <span className="font-bold">{current.initials}</span>
          <span className="text-slate-300">הצטרף</span>
          <span aria-hidden="true">🔥</span>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 text-slate-400 hover:text-white text-xs leading-none w-5 h-5 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
          aria-label="סגור"
          data-testid="joined-bubble-close"
        >
          ×
        </button>
      </div>
    </div>
  );
};

/* ── Hero avatar trust stack (real joiners + real count from /api/payment/recent-club-joiners) ── */
const HeroJoinerStack = () => {
  const [data, setData] = useState({ joiners: [], total_count: 0 });
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/payment/recent-club-joiners?limit=5`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setData({ joiners: (d.joiners || []).slice(0, 5), total_count: d.total_count || 0 }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Hide the stack entirely if no real signups exist — never show fake initials
  if (data.total_count === 0) return null;

  const displayCount = data.total_count >= 100 ? `${Math.floor(data.total_count / 10) * 10}+` : `${data.total_count}`;

  return (
    <div className="flex items-center gap-3" data-testid="hero-joiner-stack">
      <div className="flex -space-x-2 rtl:space-x-reverse">
        {data.joiners.map((j, i) => (
          <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C5366A] to-[#ff6b9d] text-white text-[10px] font-black flex items-center justify-center border-2 border-[#0f0f13] shadow-md">
            {j.initials.replace(/\./g, '').replace(/\s+/g, '')}
          </div>
        ))}
      </div>
      <div>
        <div className="flex items-center gap-1 mb-0.5" aria-label="דירוג 5 כוכבים">
          {[0, 1, 2, 3, 4].map((i) => (
            <svg key={i} viewBox="0 0 20 20" className="w-3.5 h-3.5 text-amber-400 fill-current" aria-hidden="true">
              <path d="M10 1.5l2.7 5.5 6.05.85-4.38 4.27 1.03 6.03L10 15.27l-5.4 2.88 1.03-6.03L1.25 7.85 7.3 7z" />
            </svg>
          ))}
        </div>
        <p className="text-slate-300 text-[11px] md:text-xs">
          <span className="text-white font-bold" data-testid="hero-joiner-count">{displayCount} משווקים</span> כבר הצטרפו
        </p>
      </div>
    </div>
  );
};

/* ── Hero stats row — obsolete (replaced by trial offer card) ── */

const FaqAccordion = () => {
  const [open, setOpen] = useState(null);
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_DATA.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
    inLanguage: 'he-IL',
  };
  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <div className="space-y-3">
        {FAQ_DATA.map((item, i) => (
          <div key={i} className="bg-white border border-[#C5366A]/10 rounded-xl overflow-hidden transition-all">
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center justify-between p-5 text-right hover:bg-[#FDF8FA] transition-colors"
              data-testid={`faq-${i}`}
            >
              <span className="font-bold text-[#2D2D2D] text-base md:text-lg">{item.q}</span>
              <ChevronDown className={`w-5 h-5 text-[#C5366A] flex-shrink-0 mr-3 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${open === i ? 'max-h-96 pb-5 px-5' : 'max-h-0'}`}>
              <p className="text-[#4A4A4A] leading-relaxed">{item.a}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

/* ── ROAS Calculator ── */
/* ── Animated number that counts up smoothly ── */

const JoinClubPage = () => {
  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get('status');

  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '' });
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [promo, setPromo] = useState(null); // { promo_price, regular_price, promo_start, promo_end, active }
  const [trialCfg, setTrialCfg] = useState(null); // { mode, price_incl_vat, duration_hours }
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    // Fetch trial config once — decides whether to expose the "try for ₪X" CTA
    fetch(`${API_URL}/api/config/trial`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setTrialCfg(d))
      .catch(() => {});
  }, []);

  const testimonials = [
    { image: 'https://customer-assets.emergentagent.com/job_a14f0ac9-161f-49fc-8844-e6d641b838ba/artifacts/hnnzmmij_%D7%A2%D7%93%D7%95%D7%AA%201.jpg', alt: 'עדות מבוגר התכנית' },
    { image: 'https://customer-assets.emergentagent.com/job_a14f0ac9-161f-49fc-8844-e6d641b838ba/artifacts/gefxri19_%D7%A2%D7%93%D7%95%D7%AA%202.jpg', alt: 'עדות מבוגר התכנית' },
    { image: 'https://customer-assets.emergentagent.com/job_85dfdac8-e6a4-4fa8-9b46-f1a7e5073a2b/artifacts/0tqjkb8j_%D7%A2%D7%93%D7%95%D7%AA%20%D7%99%D7%95%D7%A1%D7%99%20%D7%90%D7%A1%D7%90.jpg', alt: 'עדות יוסי אסא' },
    { image: 'https://customer-assets.emergentagent.com/job_85dfdac8-e6a4-4fa8-9b46-f1a7e5073a2b/artifacts/91ayqnys_%D7%AA%D7%9E%D7%95%D7%A0%D7%94%20%D7%A9%D7%9C%20WhatsApp%E2%80%8F%202025-04-07%20%D7%91%D7%A9%D7%A2%D7%94%2011.07.11_495e17ce.jpg', alt: 'תוצאות: 4000 השקעה - 10,000 נכנס' },
  ];

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    // Fetch active promotion
    fetch(`${API_URL}/api/config/promotions/club_membership`)
      .then(r => r.json())
      .then(data => {
        if (data.active && data.promo_start && data.promo_end) {
          const nowJlm = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jerusalem' }));
          const start = new Date(data.promo_start);
          const end = new Date(data.promo_end);
          if (nowJlm >= start && nowJlm <= end) {
            setPromo({ ...data, isActive: true });
          } else if (nowJlm < start) {
            setPromo({ ...data, isActive: false });
          }
        }
      })
      .catch(() => {});
  }, []);

  // Promo countdown
  const [promoTimeLeft, setPromoTimeLeft] = useState(null);
  useEffect(() => {
    if (!promo?.isActive || !promo?.promo_end) return;
    const calc = () => {
      const nowJlm = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jerusalem' }));
      const end = new Date(promo.promo_end);
      const diff = end.getTime() - nowJlm.getTime();
      if (diff <= 0) return { expired: true };
      return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        expired: false,
      };
    };
    setPromoTimeLeft(calc());
    const iv = setInterval(() => setPromoTimeLeft(calc()), 1000);
    return () => clearInterval(iv);
  }, [promo]);

  const isPromoActive = promo?.isActive && promoTimeLeft && !promoTimeLeft.expired;
  const currentPrice = isPromoActive ? promo.promo_price : (promo?.regular_price || 299);

  const handleChange = (e) => {
    if (e.target.name === 'phone' && phoneError) setPhoneError('');
    const value = e.target.name === 'phone' ? formatPhoneInput(e.target.value) : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const openPaymentModal = () => setShowPaymentModal(true);

  const scrollToCheckout = () => {
    document.getElementById('checkout')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    const phoneErr = validatePhone(formData.phone); if (phoneErr) { setPhoneError(phoneErr); return; }

    setIsSubmitting(true);
    setError(null);

    try {
      await fetch(`${API_URL}/api/leads/affiliate-club`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'join_club' }),
      });

      const paymentResponse = await fetch(`${API_URL}/api/payment/generate-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
        }),
      });

      const paymentData = await paymentResponse.json();

      if (paymentData.success && paymentData.payment_link) {
        trackFBEvent('Lead', { content_name: 'Join Club', content_category: 'affiliate-club' });
        trackFBEvent('InitiateCheckout', { value: currentPrice, currency: 'ILS', content_name: `Club Membership ${currentPrice}+VAT Monthly` });
        localStorage.setItem('club_questionnaire_lead', JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
        }));
        window.location.href = paymentData.payment_link;
        return;
      } else {
        setError(paymentData.error || 'שגיאה ביצירת לינק תשלום');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('שגיאה בשליחת הטופס. נסו שוב.');
    }
    setIsSubmitting(false);
  };

  /* ══════════════════ PAYMENT SUCCESS ══════════════════ */
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-[#0f0f0f] text-white">
        <main className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6 text-center mb-16" dir="rtl">
            <h1 className="text-4xl md:text-5xl font-black leading-tight mb-6">
              התשלום אושר. <span className="text-[#C5366A]">ברוך הבא לחדר המלחמה.</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              עשית עכשיו את הצעד הראשון בדרך החוצה מכלוב הריטיינרים. החשבונית ופרטי הגישה למועדון עושים את דרכם אליך למייל ברגעים אלו.
            </p>
          </div>
          <div className="max-w-3xl mx-auto px-6 mb-16" dir="rtl">
            <p className="text-center text-[#C5366A] font-bold text-lg md:text-xl mb-6">
              עצור הכל. צפה בסרטון הזה לפני שאתה ממשיך לשלב הבא
            </p>
            <div className="aspect-video rounded-2xl overflow-hidden border-2 border-gray-800 shadow-2xl shadow-[#C5366A]/10">
              <iframe src="https://www.youtube.com/embed/TvgZuC14ha0" title="ברוכים הבאים למועדון" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
            </div>
          </div>
          <div className="max-w-2xl mx-auto px-6 text-center mb-16" dir="rtl">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">הצעד הבא והאחרון שלך:</h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              הדופק של המועדון שלנו פועם בתוך קבוצת הווצאפ הסגורה של חברי הקהילה. זה המקום שבו אנחנו מפרסמים את הצעות האפיליאייט (Offers) החדשות, את הלינקים למפגשי הזום השבועיים, ושם אתה יכול להתייעץ איתנו ועם שאר התותחים שבמועדון בזמן אמת. אם אתה לא בקבוצה, <strong className="text-white">אתה מפספס 90% מהערך של המועדון.</strong>
            </p>
            <a href="https://chat.whatsapp.com/JbwwRqYHJPP0XK79Vd8Vws" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-[#1fb855] transition-all shadow-lg shadow-[#25D366]/30 hover:shadow-xl hover:scale-105"
              data-testid="thankyou-whatsapp-btn">
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              <span>הכנס אותי עכשיו לווצאפ של המועדון</span>
            </a>
          </div>
          <div className="max-w-2xl mx-auto px-6 text-center mb-20" dir="rtl">
            <div className="border-t border-gray-800 pt-10">
              <h3 className="text-lg font-semibold text-gray-300 mb-3">מה קורה עכשיו עם התכנים הדיגיטליים?</h3>
              <p className="text-gray-500 leading-relaxed">כל התכנים נשמרים בצורה מרוכזת בתיקייה פרטית שפתוחה לחברי המועדון בלבד, לינק לתיקייה מופיע בתיאור של קבוצת הווצאפ.</p>
            </div>
          </div>
          <div className="max-w-3xl mx-auto px-6" dir="rtl">
            <div className="border-t border-gray-800 pt-16">
              <div className="text-center mb-10">
                <p className="text-[#C5366A] font-bold text-sm tracking-widest uppercase mb-3">שלב אחרון</p>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">שאלון חזון ומיינדסט</h2>
                <p className="text-gray-400 leading-relaxed max-w-xl mx-auto">מלא את השאלון הקצר הזה כדי שנוכל להתאים לך את חווית המועדון בצורה מדויקת. השאלון לוקח כ-5 דקות.</p>
              </div>
              <a href="/club-questionnaire" className="flex items-center justify-center gap-3 bg-[#C5366A] text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-[#a82d57] transition-all shadow-lg shadow-[#C5366A]/30 hover:shadow-xl hover:scale-[1.03] mx-auto w-fit" data-testid="questionnaire-link">
                <span>מלא את השאלון</span>
                <ArrowLeft className="w-6 h-6" />
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  /* ══════════════════ PAYMENT FAILURE ══════════════════ */
  if (paymentStatus === 'failure') {
    return (
      <div className="min-h-screen bg-[#FDF8FA]">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-2xl mx-auto px-6 text-center" dir="rtl">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-8 border-2 border-red-500">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D2D2D] mb-6">התשלום לא הושלם</h1>
            <p className="text-[#6B6B6B] text-lg mb-8">משהו השתבש בתהליך התשלום. אל דאגה - לא חויבתם.</p>
            <a href="/join-club" className="inline-flex items-center gap-3 bg-[#C5366A] text-white px-10 py-5 font-semibold text-xl hover:bg-[#C5366A]/90 transition-colors">
              לנסות שוב
              <ArrowLeft className="w-6 h-6" />
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  /* ══════════════════ MAIN SALES PAGE ══════════════════ */
  return (
    <div className="min-h-screen bg-[#FDF8FA]">
      <SEO
        title="מועדון המשווקים המקצועי"
        description="הצטרף למועדון המשווקים המקצועי מבית מרד הפיפיסיסטים. הצעות אפיליאייט בלעדיות, ארכיטקטורה שיווקית, וקהילה של אנשי דיגיטל מנוסים."
        path="/join-club"
      />
      <Header />

      <main id="main-content">

        {/* ═══════════ HERO ═══════════ */}
        <section className="relative overflow-hidden bg-[#0f0f13]" data-testid="hero-section">
          {/* Decorative background */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C5366A]/12 rounded-full blur-[120px] translate-x-1/3 -translate-y-1/3 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[450px] h-[450px] bg-[#C5366A]/8 rounded-full blur-[100px] -translate-x-1/3 translate-y-1/3 pointer-events-none" />

          <div className="relative max-w-6xl mx-auto px-5 md:px-6 pt-24 md:pt-28 pb-14 md:pb-20" dir="rtl">
            <div className="grid lg:grid-cols-12 gap-8 md:gap-10 lg:gap-14 items-center">

              {/* ── Right column (text + CTA) ── */}
              <div className="lg:col-span-7 text-center lg:text-right order-2 lg:order-1">

                {/* Kicker */}
                <div className="inline-flex items-center gap-2 bg-[#C5366A]/12 text-[#ff6b9d] text-[11px] md:text-xs font-bold px-3.5 py-1.5 rounded-full mb-5 md:mb-7" data-testid="hero-eyebrow">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5366A] animate-pulse" />
                  <span>למומחי PPC, מנהלי קמפיינים ומדיה באיירס מנוסים בלבד</span>
                </div>

                {/* H1 */}
                <h1 className="text-[28px] sm:text-4xl lg:text-5xl xl:text-[3.4rem] font-black text-white leading-[1.15] mb-4 md:mb-6" data-testid="hero-h1">
                  תפסיק להסתפק בריטיינר —{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-l from-[#C5366A] to-[#ff6b9d]">
                    תתחיל לבנות נכסי דאטה בבעלותך המלאה
                  </span>
                </h1>

                {/* H2 */}
                <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-7 md:mb-9 max-w-2xl lg:mx-0 mx-auto" data-testid="hero-h2">
                  פרוטוקול "הקופסה השחורה" חושף את הארכיטקטורה שמנתקת אנשי דיגיטל מהתלות בריטיינרים מוגבלים ומאפשרת לך לייצר <strong className="text-white">5–6 ספרות בחודש מנכסים שלך</strong> — בלי שירות לקוחות, בלי מלאי ובלי להתקשר ללידים.
                </p>

                {/* Carousel — mobile only */}
                <div className="block lg:hidden mb-7">
                  <HeroCarousel compact />
                </div>

                {/* ── TRIAL OFFER CARD — the primary conversion card ── */}
                <div
                  className="relative overflow-hidden bg-gradient-to-br from-[#1a1a24] to-[#0f0f13] shadow-[0_20px_60px_-20px_rgba(197,54,106,0.5)] rounded-3xl p-6 md:p-8 text-right"
                  data-testid="hero-trial-card"
                >
                  <div className="absolute -top-16 -left-16 w-52 h-52 bg-[#C5366A]/25 rounded-full blur-3xl pointer-events-none" />
                  {trialCfg && (
                    <div className="relative">
                      <div className="inline-flex items-center gap-1.5 text-amber-400 text-[11px] md:text-xs font-bold mb-3">
                        <span>🔥</span>
                        <span>הצעה מיוחדת לזמן מוגבל: התחל בתקופת ניסיון</span>
                      </div>
                      <p className="text-white text-lg md:text-xl font-bold leading-snug mb-1.5" data-testid="hero-trial-headline">
                        קבל גישה מלאה למועדון המשווקים ל־<span className="text-[#ff6b9d]">
                          {trialCfg.duration_hours >= 24 ? `${Math.round(trialCfg.duration_hours / 24)} ימים` : `${trialCfg.duration_hours} שעות`}
                        </span>
                      </p>
                      <p className="text-slate-400 text-sm md:text-base mb-6">
                        {trialCfg.mode === 'paid' ? (
                          <>בתשלום סמלי של <span className="text-white font-black text-lg">₪{trialCfg.price_incl_vat}</span> בלבד</>
                        ) : (
                          <><span className="text-emerald-400 font-black text-lg">חינם לחלוטין</span> · ללא כרטיס אשראי</>
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={() => { setShowTrialModal(true); trackFBEvent('InitiateCheckout', { content_name: 'Trial Signup - Hero' }); }}
                        className="group w-full flex items-center justify-center gap-3 bg-gradient-to-l from-[#C5366A] to-[#e84393] text-white px-6 md:px-9 py-4 md:py-5 rounded-2xl font-black text-base md:text-lg shadow-[0_14px_40px_-8px_rgba(197,54,106,0.7)] hover:shadow-[0_18px_50px_-8px_rgba(197,54,106,0.95)] hover:-translate-y-0.5 transition-all"
                        data-testid="hero-trial-cta"
                      >
                        <span>
                          {trialCfg.mode === 'paid'
                            ? `👉 התחל את תקופת הניסיון ב־₪${trialCfg.price_incl_vat} עכשיו`
                            : '👉 התחל את הניסיון החינמי עכשיו'}
                        </span>
                        <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                      </button>
                      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-slate-400 text-[11px] md:text-xs mt-4">
                        <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-400" /> ללא התחייבות</span>
                        <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-400" /> ביטול בקליק בכל עת</span>
                        <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-400" /> תזכורת לפני חיוב המנוי</span>
                      </div>
                    </div>
                  )}
                  {!trialCfg && (
                    <div className="relative">
                      <p className="text-white text-lg md:text-xl font-bold mb-2">
                        הצטרפות ישירה למועדון המשווקים
                      </p>
                      <p className="text-slate-400 text-sm md:text-base mb-6">
                        <span className="text-white font-black text-lg">₪{currentPrice}</span> / חודש + מע"מ · ביטול בכל עת
                      </p>
                      <button
                        onClick={scrollToCheckout}
                        className="group inline-flex items-center gap-3 bg-gradient-to-l from-[#C5366A] to-[#e84393] text-white px-7 md:px-9 py-4 md:py-5 rounded-2xl font-black text-base md:text-lg shadow-[0_14px_40px_-8px_rgba(197,54,106,0.7)] hover:shadow-[0_18px_50px_-8px_rgba(197,54,106,0.95)] hover:-translate-y-0.5 transition-all w-full sm:w-auto justify-center"
                        data-testid="hero-cta"
                      >
                        <span>👉 הצטרף עכשיו למועדון</span>
                        <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                      </button>
                    </div>
                  )}

                  {/* Promo countdown pill inside the trial card when applicable */}
                  {isPromoActive && promoTimeLeft && (
                    <div className="relative mt-4 inline-flex items-center gap-2 bg-amber-500/10 text-amber-300 text-[11px] font-bold px-3 py-1.5 rounded-full" data-testid="hero-promo-badge">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                      <span>מחיר מלא חוזר ל־₪{promo.regular_price} בעוד {promoTimeLeft.days > 0 ? `${promoTimeLeft.days}י ` : ''}{String(promoTimeLeft.hours).padStart(2,'0')}:{String(promoTimeLeft.minutes).padStart(2,'0')}:{String(promoTimeLeft.seconds).padStart(2,'0')}</span>
                    </div>
                  )}
                </div>

                {/* Micro social proof under the CTA card */}
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-slate-400 text-xs md:text-sm mt-5" data-testid="hero-micro-proof">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-amber-400">⭐</span>
                    <span><span className="text-white font-bold">200+</span> משווקים פעילים בקהילה</span>
                  </span>
                  <span className="text-slate-700 hidden md:inline">|</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span>💰</span>
                    <span><span className="text-white font-bold">40,000,000+ ₪</span> עמלות נטו לחברים</span>
                  </span>
                </div>

                {/* Real-signup trust stack (KEPT — social proof feature) */}
                <div className="mt-5">
                  <HeroJoinerStack />
                </div>
              </div>

              {/* ── Left column (carousel) - desktop only ── */}
              <div className="hidden lg:block lg:col-span-5 order-1 lg:order-2">
                <div className="relative">
                  <div className="absolute -inset-3 bg-gradient-to-br from-[#C5366A]/40 via-transparent to-transparent rounded-3xl blur-2xl opacity-70 pointer-events-none" />
                  <div className="relative">
                    <HeroCarousel compact />
                  </div>
                </div>
              </div>
            </div>

            <p className="text-slate-600 text-[11px] text-center mt-10 md:mt-14">דף זה מנוסח בלשון זכר מטעמי נוחות בלבד אך פונה לשני המינים.</p>
          </div>
        </section>

        {/* ═══════════ PROBLEM ═══════════ */}
        <section className="py-16 md:py-24 bg-[#F8EEF2]" data-testid="problem-section">
          <div className="max-w-5xl mx-auto px-5 md:px-6" dir="rtl">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-black text-[#2D2D2D] leading-tight text-center mb-6" data-testid="problem-h2">
                המתמטיקה האכזרית של מודל הריטיינר:{' '}
                <span className="text-[#C5366A]">למה אתה עובד קשה מדי ונשאר עם פירורים?</span>
              </h2>
            </Reveal>

            <Reveal delay={80}>
              <p className="text-[#4A4A4A] text-base md:text-lg leading-[1.85] text-center max-w-3xl mx-auto mb-12 md:mb-14">
                אתה יושב מול הביזנס מנג'ר, מביא ROAS מטורף ומוריד CPA. בעל העסק שאתה מנהל לו את הקמפיין מרוויח מיליונים ואוסף דאטה ששווה זהב — ואתה? אתה מקבל חשבונית ריטיינר קבועה של 3,000 עד 10,000 ש"ח, וחי בחרדה מתמדת משיחת הטלפון ביום שישי שבה הלקוח יספר שהליד "קצת התייקר".
              </p>
            </Reveal>

            {/* Comparison table — borderless, color-blocked (light grid vs dark card) */}
            <Reveal delay={160}>
              <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-testid="problem-comparison">
                {/* Legacy model card */}
                <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm">
                  <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-600 text-[10px] md:text-xs font-bold px-3 py-1 rounded-full mb-4">
                    <span>❌</span>
                    <span>מודל הריטיינר הישן</span>
                  </div>
                  <h3 className="text-[#2D2D2D] text-lg md:text-xl font-bold mb-5">פועל דיגיטלי</h3>
                  <ul className="space-y-3 md:space-y-4 text-sm md:text-base">
                    {[
                      { k: 'מקור ההכנסה', v: 'שעתי / ריטיינר מוגבל בתקרה' },
                      { k: 'בעלות על הדאטה', v: '100% שייך ללקוח' },
                      { k: 'עלות רימרקטינג', v: 'משלמים מחדש לפלטפורמות על כל קליק' },
                      { k: 'תפעול ושירות', v: 'ניהול לקוחות, שיחות זום ודוחות חודשיים' },
                      { k: 'יציבות עסקית', v: 'לקוח עזב = ההכנסה יורדת לאפס' },
                    ].map((row, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-slate-400 flex-shrink-0 mt-0.5">•</span>
                        <div>
                          <p className="text-slate-500 text-[11px] md:text-xs font-bold uppercase tracking-wider mb-0.5">{row.k}</p>
                          <p className="text-slate-700 leading-snug">{row.v}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Black Box model card — winning side */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#1a1a24] to-[#0f0f13] rounded-3xl p-6 md:p-8 shadow-[0_20px_60px_-20px_rgba(197,54,106,0.4)]">
                  <div className="absolute -top-16 -right-16 w-52 h-52 bg-[#C5366A]/20 rounded-full blur-3xl pointer-events-none" />
                  <div className="relative">
                    <div className="inline-flex items-center gap-2 bg-[#C5366A]/20 text-[#ff6b9d] text-[10px] md:text-xs font-bold px-3 py-1 rounded-full mb-4">
                      <span>✓</span>
                      <span>מודל הקופסה השחורה</span>
                    </div>
                    <h3 className="text-white text-lg md:text-xl font-bold mb-5">בעל נכסים</h3>
                    <ul className="space-y-3 md:space-y-4 text-sm md:text-base">
                      {[
                        { k: 'מקור ההכנסה', v: 'אקספוננציאלי מתגמול מבוסס ביצועים (CPA)' },
                        { k: 'בעלות על הדאטה', v: '100% בבעלותך המלאה' },
                        { k: 'עלות רימרקטינג', v: 'CPA 0 ש"ח — פנייה חופשית במייל/ווצאפ' },
                        { k: 'תפעול ושירות', v: '0 שירות לקוחות, 0 שיחות מכירה' },
                        { k: 'יציבות עסקית', v: 'הנכס והרשימה נשארים אצלך לתמיד' },
                      ].map((row, i) => (
                        <li key={i} className="flex gap-3">
                          <Check className="w-4 h-4 text-[#ff6b9d] flex-shrink-0 mt-1" />
                          <div>
                            <p className="text-[#ff6b9d]/80 text-[11px] md:text-xs font-bold uppercase tracking-wider mb-0.5">{row.k}</p>
                            <p className="text-white leading-snug">{row.v}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════════ SOLUTION ═══════════ */}
        <section className="py-16 md:py-24 bg-white" data-testid="solution-section">
          <div className="max-w-5xl mx-auto px-5 md:px-6" dir="rtl">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-black text-[#2D2D2D] leading-tight text-center mb-4" data-testid="solution-h2">
                איך השיטה עובדת?{' '}
                <span className="text-[#C5366A]">100% שיווק ודאטה — 0% כאב ראש אופרטיבי</span>
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <p className="text-[#4A4A4A] text-base md:text-lg leading-[1.85] text-center max-w-3xl mx-auto mb-12">
                במקום לעשות שיווק שותפים מיושן (״לזרוק טראפיק״ ללינק אפיליאייט ולשרוף כסף), פרוטוקול הקופסה השחורה מבוסס על <strong>ארכיטקטורת גשר</strong>:
              </p>
            </Reveal>

            {/* Funnel visualization — borderless color-blocked pipe */}
            <Reveal delay={140}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-2 mb-14" data-testid="solution-funnel">
                {[
                  { label: 'מודעה ממומנת', sub: 'PPC / Meta / Google', bg: 'bg-slate-100', text: 'text-[#2D2D2D]' },
                  { label: 'דף לידים של הנכס שלך', sub: 'בבעלותך המלאה', bg: 'bg-[#F8EEF2]', text: 'text-[#2D2D2D]' },
                  { label: 'רשימת תפוצה / ווצאפ שלך', sub: 'CPA 0 ש"ח', bg: 'bg-[#C5366A]/15', text: 'text-[#2D2D2D]' },
                  { label: 'הצעת VIP ממירה', sub: 'עמלות גבוהות', bg: 'bg-[#0f0f13]', text: 'text-white' },
                ].map((step, i) => (
                  <div key={i} className="flex flex-col md:flex-row items-stretch">
                    <div className={`${step.bg} ${step.text} rounded-2xl p-4 md:p-5 flex-1 flex flex-col justify-center text-center`}>
                      <p className="font-black text-sm md:text-base leading-tight mb-1">{step.label}</p>
                      <p className={`text-[11px] md:text-xs ${step.text === 'text-white' ? 'text-slate-400' : 'text-[#6B6B6B]'}`}>{step.sub}</p>
                    </div>
                    {i < 3 && (
                      <div className="hidden md:flex items-center justify-center px-1">
                        <ArrowLeft className="w-4 h-4 text-[#C5366A]" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Reveal>

            {/* 3 numbered steps */}
            <div className="grid md:grid-cols-3 gap-5 md:gap-8">
              {[
                { n: 1, t: 'אתה בונה דף איסוף לידים בבעלותך', d: 'כל גולש שנכנס נרשם ראשית לתוך המערכת שלך — הדאטה שלך.' },
                { n: 2, t: 'הדאטה נשארת אצלך', d: 'גם אם הגולש לא קנה ביום הראשון, אתה מתקשר איתו שוב ושוב בעלות אפס.' },
                { n: 3, t: 'אנחנו מטפלים בכל השאר', d: 'מועדון המשווקים מספק לך הצעות (Offers) סגורות בעמלות גבוהות. אתה רק מזרים טראפיק וגוזר את העמלה.' },
              ].map((step, i) => (
                <Reveal key={step.n} delay={i * 100}>
                  <div className="text-center md:text-right">
                    <div className="inline-flex items-center justify-center w-11 h-11 bg-[#C5366A] text-white text-lg font-black rounded-2xl mb-4 shadow-lg shadow-[#C5366A]/30">
                      {step.n}
                    </div>
                    <h3 className="font-bold text-[#2D2D2D] text-lg md:text-xl mb-2 leading-tight">{step.t}</h3>
                    <p className="text-[#6B6B6B] text-sm md:text-base leading-relaxed">{step.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════ OFFERS CAROUSEL — "מה משווקים?" (iter66n) ═══════════ */}
        <OffersMarketedSection
          scrollToCheckout={scrollToCheckout}
          trialCfg={trialCfg}
          onOpenTrial={() => { setShowTrialModal(true); trackFBEvent('InitiateCheckout', { content_name: 'Trial Signup - Offer Modal' }); }}
        />

        {/* ═══════════ WHAT'S INCLUDED ═══════════ */}
        <section className="py-16 md:py-24 bg-[#0f0f13] relative overflow-hidden" data-testid="included-section">
          <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-[#C5366A]/10 rounded-full blur-[120px] pointer-events-none" />
          <div className="relative max-w-6xl mx-auto px-5 md:px-6" dir="rtl">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-black text-white leading-tight text-center mb-12 md:mb-16" data-testid="included-h2">
                חדר המצב של אנשי ה־PPC:{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-l from-[#C5366A] to-[#ff6b9d]">
                  כל מה שאתה מקבל במועדון
                </span>
              </h2>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="included-grid">
              {[
                {
                  icon: '💻',
                  title: 'איזור מנויים מתקדם אונליין',
                  desc: 'ערוץ דיסקרטי לשיתוף פעולה, אנליטיקה מהשטח ועדכונים בזמן אמת — הכל במתחם חברים סגור.',
                },
                {
                  icon: '📞',
                  title: 'מפגשי זום שבועיים',
                  desc: 'כל יום חמישי ב־11:00: ניתוח קייס סטאדיז בלייב, אופטימיזציה למשפכים וסקירת הצעות חמות.',
                },
                {
                  icon: '📚',
                  title: 'מאגר הידע של המועדון',
                  desc: 'ספריית הקלטות של כל המפגשים, לתובנות מפעילות אמיתית וניסיון של עשרות שנים בתחום.',
                },
                {
                  icon: '🔒',
                  title: 'קטלוג הצעות VIP בלעדיות',
                  desc: 'גישה להצעות סגורות שנבדקו בשטח עם יחסי המרה גבוהים ועמלות מדהימות.',
                },
                {
                  icon: '🎯',
                  title: 'ארכיטקטורה מוכנה + ליווי',
                  desc: 'עבודה צמודה איתך על דיוק דפי הנחיתה, האוטומציות ושיפור יחס ההמרה עד הגעה לרווחיות.',
                  wide: true,
                },
              ].map((feature, i) => (
                <Reveal key={i} delay={i * 60} className={feature.wide ? 'md:col-span-2 lg:col-span-1' : ''}>
                  <div className="h-full bg-[#1a1a24] hover:bg-[#20202e] transition-colors rounded-3xl p-6 md:p-7 shadow-[0_10px_40px_-15px_rgba(197,54,106,0.3)]">
                    <div className="text-3xl md:text-4xl mb-4">{feature.icon}</div>
                    <h3 className="font-bold text-white text-lg md:text-xl mb-2.5 leading-tight">{feature.title}</h3>
                    <p className="text-slate-400 text-sm md:text-base leading-relaxed">{feature.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════ NUMBERS + TESTIMONIAL QUOTE ═══════════ */}
        <section className="py-16 md:py-24 bg-[#FDF8FA]" data-testid="numbers-section">
          <div className="max-w-6xl mx-auto px-5 md:px-6" dir="rtl">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-black text-[#2D2D2D] leading-tight text-center mb-3" data-testid="numbers-h2">
                המספרים מדברים
              </h2>
              <p className="text-[#6B6B6B] text-base md:text-lg text-center mb-12 md:mb-14">תוצאות מהשטח של חברי המועדון</p>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-12">
              <Reveal delay={60}>
                <div className="bg-white rounded-3xl p-8 md:p-10 text-center shadow-sm">
                  <div className="text-4xl md:text-5xl mb-3">💵</div>
                  <div className="text-4xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-l from-[#C5366A] to-[#ff6b9d] leading-none mb-3" data-testid="numbers-commissions">
                    ₪41,000,553+
                  </div>
                  <p className="text-[#4A4A4A] text-sm md:text-base leading-relaxed">
                    עמלות נטו ששולמו ישירות לחברי המועדון
                  </p>
                </div>
              </Reveal>
              <Reveal delay={140}>
                <div className="bg-[#0f0f13] text-white rounded-3xl p-8 md:p-10 text-center shadow-[0_20px_60px_-20px_rgba(197,54,106,0.4)]">
                  <div className="text-4xl md:text-5xl mb-3">👥</div>
                  <div className="text-4xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-l from-[#C5366A] to-[#ff6b9d] leading-none mb-3" data-testid="numbers-marketers">
                    300+
                  </div>
                  <p className="text-slate-400 text-sm md:text-base leading-relaxed">
                    אנשי דיגיטל ומדיה באיירס שכבר פועלים במודל הנכסים
                  </p>
                </div>
              </Reveal>
            </div>

            {/* Uri quote — pull-quote style, borderless */}
            <Reveal delay={220}>
              <blockquote className="bg-white rounded-3xl p-6 md:p-10 shadow-sm max-w-3xl mx-auto text-center" data-testid="numbers-quote">
                <div className="text-[#C5366A] text-5xl md:text-6xl font-black leading-none mb-3">״</div>
                <p className="text-[#2D2D2D] text-lg md:text-xl leading-[1.8] font-medium mb-6">
                  אספתי לידים לבוט ולרשימה לפני שהעברתי להצעה, ובניתי קהילה של עשרות אלפי נמענים. היום אני שולח הצעות בלחיצת כפתור ומגיע ל־<strong className="text-[#C5366A]">60,000 ש"ח בחודש</strong> ללא עלות מדיה נוספת.
                </p>
                <cite className="not-italic text-[#6B6B6B] text-sm md:text-base font-bold">— אורי, חבר מועדון המשווקים</cite>
              </blockquote>
            </Reveal>
          </div>
        </section>

        {/* ═══════════ TESTIMONIALS CAROUSEL (KEPT) ═══════════ */}
        <section className="py-16 bg-[#F8EEF2]" data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto px-6" dir="rtl">
            <h2 className="text-2xl md:text-3xl font-black text-[#2D2D2D] text-center mb-8">
              עדויות אמיתיות של חברי המועדון
            </h2>
            <div className="relative max-w-xl mx-auto" onMouseEnter={() => setIsAutoPlaying(false)} onMouseLeave={() => setIsAutoPlaying(true)}>
              <div className="overflow-hidden rounded-2xl shadow-xl bg-white">
                <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(${currentSlide * 100}%)` }}>
                  {testimonials.map((t, i) => (
                    <div key={i} className="w-full flex-shrink-0">
                      <img src={t.image} alt={t.alt} className="w-full h-auto object-cover" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={prevSlide} className="absolute top-1/2 -translate-y-1/2 -left-4 md:-left-6 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#C5366A] hover:bg-[#C5366A] hover:text-white transition-all" aria-label="הקודם"><ChevronLeft className="w-5 h-5 md:w-6 md:h-6" /></button>
              <button onClick={nextSlide} className="absolute top-1/2 -translate-y-1/2 -right-4 md:-right-6 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#C5366A] hover:bg-[#C5366A] hover:text-white transition-all" aria-label="הבא"><ChevronRight className="w-5 h-5 md:w-6 md:h-6" /></button>
              <div className="flex justify-center gap-2.5 mt-6">
                {testimonials.map((_, i) => (
                  <button key={i} onClick={() => setCurrentSlide(i)} className={`rounded-full transition-all duration-300 ${i === currentSlide ? 'w-8 h-3 bg-[#C5366A]' : 'w-3 h-3 bg-white/70 hover:bg-[#C5366A]/50'}`} aria-label={`עדות ${i + 1}`} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ FAQ ═══════════ */}
        <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-5 md:px-6" dir="rtl">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-black text-[#2D2D2D] leading-tight text-center mb-10 md:mb-12">
                שאלות נפוצות
              </h2>
            </Reveal>
            <FaqAccordion />
          </div>
        </section>

        {/* ═══════════ FINAL OFFER / CHECKOUT ═══════════ */}
        <section
          className="py-16 md:py-24 bg-gradient-to-br from-[#0f0f13] via-[#1a1a24] to-[#0f0f13] relative overflow-hidden"
          id="checkout"
          data-testid="checkout-section"
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#C5366A]/15 rounded-full blur-[140px] pointer-events-none" />
          <div className="relative max-w-3xl mx-auto px-5 md:px-6" dir="rtl">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-black text-white leading-tight text-center mb-4" data-testid="final-h2">
                מוכן לצאת ממלכודת הריטיינר ולהפוך ל<span className="text-transparent bg-clip-text bg-gradient-to-l from-[#C5366A] to-[#ff6b9d]">בעל נכסים</span>?
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <p className="text-slate-400 text-center text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10 md:mb-12">
                ניסיון קודם בשיווק ממומן נדרש. אנחנו לא מוכרים קורסים למתחילים —{' '}
                <strong className="text-white">אנחנו מספקים ארכיטקטורה וסביבת עבודה למקצוענים.</strong>
              </p>
            </Reveal>

            {/* Trial offer card OR direct-join pricing card */}
            <Reveal delay={140}>
              {trialCfg ? (
                <div className="bg-white rounded-3xl p-6 md:p-10 shadow-[0_30px_80px_-20px_rgba(197,54,106,0.6)]" data-testid="final-trial-card">
                  <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-[11px] md:text-xs font-bold px-3 py-1.5 rounded-full mb-5">
                    <span>🔥</span>
                    <span>הצעה מיוחדת לזמן מוגבל</span>
                  </div>
                  <h3 className="text-[#2D2D2D] text-xl md:text-3xl font-black leading-tight mb-3" data-testid="final-trial-headline">
                    תקופת ניסיון מיוחדת:{' '}
                    <span className="text-[#C5366A]">
                      {trialCfg.duration_hours >= 24 ? `${Math.round(trialCfg.duration_hours / 24)} ימים` : `${trialCfg.duration_hours} שעות`}
                    </span>{' '}
                    {trialCfg.mode === 'paid' ? (
                      <>ב־<span className="text-[#C5366A]">₪{trialCfg.price_incl_vat}</span> בלבד!</>
                    ) : (
                      <>— <span className="text-emerald-600">חינם לחלוטין!</span></>
                    )}
                  </h3>
                  <p className="text-[#4A4A4A] text-sm md:text-base leading-relaxed mb-7">
                    כניסה מיידית למועדון המשווקים המקצועי + גישה להקלטות המפגשים. המנוי מתחדש אוטומטית ב־<strong>₪{promo?.regular_price || 299} / חודש + מע"מ</strong>.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setShowTrialModal(true); trackFBEvent('InitiateCheckout', { content_name: 'Trial Signup - Final CTA' }); }}
                    className="group w-full flex items-center justify-center gap-3 bg-gradient-to-l from-[#C5366A] to-[#e84393] text-white px-6 md:px-9 py-5 md:py-6 rounded-2xl font-black text-base md:text-xl shadow-[0_14px_40px_-8px_rgba(197,54,106,0.6)] hover:shadow-[0_18px_50px_-8px_rgba(197,54,106,0.85)] hover:-translate-y-0.5 transition-all"
                    data-testid="final-trial-cta"
                  >
                    <span>
                      {trialCfg.mode === 'paid'
                        ? `👉 לחץ כאן להתחלת תקופת הניסיון ב־₪${trialCfg.price_incl_vat} בלבד`
                        : '👉 לחץ כאן להתחלת הניסיון החינמי'}
                    </span>
                    <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 transition-transform group-hover:-translate-x-1" />
                  </button>
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[#6B6B6B] text-[11px] md:text-sm mt-5">
                    <span className="inline-flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" /> ביטול בכל עת בלחיצת כפתור</span>
                    <span className="inline-flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" /> ללא התחייבות</span>
                    <span className="inline-flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" /> 3 התראות לפני חיוב</span>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-3xl p-6 md:p-10 shadow-[0_30px_80px_-20px_rgba(197,54,106,0.6)]" data-testid="final-direct-card">
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className="text-sm text-[#6B6B6B]">רק</span>
                    <span className="text-5xl md:text-6xl font-black text-[#C5366A]" data-testid="display-price">₪{currentPrice}</span>
                    <span className="text-[#6B6B6B]">/ לחודש + מע"מ</span>
                    {isPromoActive && promo.regular_price && (
                      <span className="text-[#999] line-through text-lg mr-2">₪{promo.regular_price}</span>
                    )}
                  </div>

                  {isPromoActive && promoTimeLeft && (
                    <div className="bg-gradient-to-l from-[#C5366A]/10 to-[#ff6b6b]/10 p-4 rounded-2xl mb-6 max-w-md mx-auto" data-testid="promo-countdown-banner">
                      <p className="text-[#C5366A] font-bold text-sm mb-2 text-center">
                        מחיר מבצע! המחיר יעלה ל־{promo.regular_price} ש"ח בעוד:
                      </p>
                      <div className="flex justify-center gap-3" dir="ltr">
                        {[
                          { v: promoTimeLeft.days, l: 'ימים' },
                          { v: promoTimeLeft.hours, l: 'שעות' },
                          { v: promoTimeLeft.minutes, l: 'דקות' },
                          { v: promoTimeLeft.seconds, l: 'שניות' },
                        ].map((b, i) => (
                          <div key={i} className="flex flex-col items-center">
                            <div className="bg-[#C5366A] text-white rounded-md w-10 h-10 flex items-center justify-center font-black text-lg tabular-nums">
                              {String(b.v).padStart(2, '0')}
                            </div>
                            <span className="text-[#6B6B6B] text-[10px] mt-1">{b.l}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <ul className="space-y-3 my-6">
                    {[
                      'גישה מלאה להצעות הסגורות של המועדון',
                      'ארכיטקטורה מוכנה + ליווי ארכיטקט אישי',
                      'חדר מצב וקהילת ווצאפ סגורה',
                      'מפגשי זום שבועיים + ארכיון הקלטות',
                    ].map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#4A4A4A]">
                        <Check className="w-5 h-5 text-[#C5366A] flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={openPaymentModal}
                    className="w-full group inline-flex items-center justify-center gap-3 bg-gradient-to-l from-[#C5366A] to-[#e84393] text-white py-5 md:py-6 rounded-2xl font-black text-base md:text-xl shadow-[0_14px_40px_-8px_rgba(197,54,106,0.6)] hover:shadow-[0_18px_50px_-8px_rgba(197,54,106,0.85)] hover:-translate-y-0.5 transition-all"
                    data-testid="checkout-cta"
                  >
                    <CreditCard className="w-5 h-5" />
                    <span>👉 הצטרף עכשיו למועדון המשווקים</span>
                  </button>

                  <p className="text-[#999] text-xs md:text-sm mt-4 flex items-center justify-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" />
                    <span>תשלום מאובטח · ביטול בכל עת · ללא התחייבות</span>
                  </p>
                </div>
              )}
            </Reveal>
          </div>
        </section>

      </main>

      {/* ═══════════════════════════════════════════════
          PAYMENT MODAL
      ═══════════════════════════════════════════════ */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPaymentModal(false)} data-testid="payment-modal">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} dir="rtl">
            <div className="bg-gradient-to-l from-[#C5366A] to-[#a82d57] px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold text-lg">הצטרפות למועדון</h3>
                <p className="text-white/70 text-sm">{currentPrice} ש"ח + מע"מ לחודש</p>
              </div>
              <button onClick={() => setShowPaymentModal(false)} className="text-white/70 hover:text-white" data-testid="modal-close"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {trialCfg?.mode === 'paid' && (
                <div className="bg-gradient-to-l from-emerald-50 to-white rounded-xl p-4 border border-emerald-100" data-testid="join-trial-cta">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">⚡</div>
                    <div className="flex-1">
                      <p className="font-bold text-[#2D2D2D] text-sm">
                        לא בטוחים? נסו טריאל {trialCfg.duration_hours >= 24 ? `${Math.round(trialCfg.duration_hours/24)} ימים` : `${trialCfg.duration_hours} שעות`} ב-₪{trialCfg.price_incl_vat}
                      </p>
                      <p className="text-xs text-[#6B6B6B] mt-0.5">
                        גישה מלאה, ובסוף התקופה המנוי מתחיל אוטומטית — עם 3 התראות לפני החיוב.
                      </p>
                      <button
                        type="button"
                        onClick={() => { setShowPaymentModal(false); setShowTrialModal(true); }}
                        className="inline-block mt-2 text-emerald-700 text-xs font-bold hover:underline"
                        data-testid="join-trial-link"
                      >
                        התחילו טריאל ב-₪{trialCfg.price_incl_vat} ←
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#6B6B6B] mb-1.5">שם מלא *</label>
                <input type="text" name="fullName" required value={formData.fullName} onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-[#2D2D2D] focus:ring-2 focus:ring-[#C5366A]/30 focus:border-[#C5366A] outline-none"
                  data-testid="modal-fullname" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6B6B6B] mb-1.5">אימייל *</label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-[#2D2D2D] focus:ring-2 focus:ring-[#C5366A]/30 focus:border-[#C5366A] outline-none"
                  data-testid="modal-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6B6B6B] mb-1.5">טלפון *</label>
                <input type="tel" name="phone" required maxLength={14} aria-invalid={!!phoneError}
                  value={formData.phone} onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-[#2D2D2D] focus:ring-2 focus:ring-[#C5366A]/30 focus:border-[#C5366A] outline-none"
                  data-testid="modal-phone" />
                <FieldError>{phoneError}</FieldError>
                <PhoneValid phone={formData.phone} />
              </div>

              <ConsentCheckbox checked={consent} onChange={setConsent} />

              {error && <p className="text-red-500 text-sm" data-testid="modal-error">{error}</p>}

              <button type="submit" disabled={!consent || isSubmitting}
                className="w-full bg-[#00C853] text-white py-4 rounded-xl font-black text-lg hover:bg-[#00b348] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                data-testid="modal-submit">
                {isSubmitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /><span>מעבר לתשלום...</span></>
                ) : (
                  <><CreditCard className="w-5 h-5" /><span>המשך לתשלום מאובטח</span></>
                )}
              </button>

              <p className="text-center text-gray-400 text-xs flex items-center justify-center gap-1">
                <Lock className="w-3 h-3" />
                <span>תשלום מאובטח באמצעות PayPlus</span>
              </p>
            </form>
          </div>
        </div>
      )}

      <Footer />
      <JoinedBubble />
      <TrialSignupModal
        open={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        prefill={{ name: formData.fullName, email: formData.email, phone: formData.phone }}
        source="join_club"
      />
    </div>
  );
};

export default JoinClubPage;
